import { createContext, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@mui/material";

const CloudinaryScriptContext = createContext();

function CloudinaryUploadWidget({ uwConfig, setPublicId, setImageUrl }) {
  const [loaded, setLoaded] = useState(false);
  const widgetRef = useRef(null);

  useEffect(() => {
    if (window.cloudinary?.createUploadWidget) {
      setLoaded(true);
      return;
    }

    const existingScript = document.getElementById("cloudinary-upload-widget");
    if (existingScript) {
      existingScript.addEventListener("load", () => setLoaded(true), { once: true });
      return;
    }

    const script = document.createElement("script");
    script.setAttribute("async", "");
    script.setAttribute("id", "cloudinary-upload-widget");
    script.src = "https://upload-widget.cloudinary.com/global/all.js";
    script.addEventListener("load", () => setLoaded(true), { once: true });
    document.body.appendChild(script);
  }, []);

  useEffect(() => {
    if (!loaded || !window.cloudinary?.createUploadWidget) return undefined;

    widgetRef.current = window.cloudinary.createUploadWidget(
      uwConfig,
      (error, result) => {
        if (!error && result?.event === "success") {
          setImageUrl(result.info.secure_url);
          setPublicId(result.info.secure_url);
        }
      }
    );

    return () => {
      widgetRef.current?.destroy?.();
      widgetRef.current = null;
    };
  }, [loaded, setImageUrl, setPublicId, uwConfig]);

  return (
    <CloudinaryScriptContext.Provider value={{ loaded }}>
      <Button
        variant="contained"
        className="cloudinary-button"
        onClick={() => widgetRef.current?.open()}
        disabled={!loaded}
        fullWidth
      >
        {loaded ? "Upload Photo" : "Loading uploader..."}
      </Button>
    </CloudinaryScriptContext.Provider>
  );
}

export { CloudinaryScriptContext };

export function CloudinaryUpload({ imageUrl, setImageUrl }) {
  const [, setPublicId] = useState(imageUrl);
  const cloudName = "dncq6xhlx";
  const uploadPreset = "xhewypte";

  const uwConfig = useMemo(() => ({
    cloudName,
    uploadPreset,
    sources: ["local", "camera", "url"],
    multiple: false,
    resourceType: "image",
    clientAllowedFormats: ["jpg", "jpeg", "png", "webp", "heic", "heif"],
    maxImageFileSize: 10000000,
    maxImageWidth: 2400,
    maxImageHeight: 2400,
    cropping: true,
    croppingAspectRatio: 1,
    croppingShowDimensions: true,
  }), [cloudName, uploadPreset]);

  return (
    <CloudinaryUploadWidget
      uwConfig={uwConfig}
      setPublicId={setPublicId}
      setImageUrl={setImageUrl}
    />
  );
}
